import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Radio, Trash2, ChevronRight, BookMarked } from 'lucide-react';
import { api } from '../api/client';
import {
  Card,
  CardContent,
  Button,
  Badge,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@databro/ui';

function statusVariant(status: string): 'default' | 'secondary' | 'destructive' | 'outline' {
  if (status === 'recording') return 'default';
  if (status === 'failed') return 'destructive';
  return 'secondary';
}

export default function Recordings() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['recordings'],
    queryFn: () => api.getRecordings({ limit: 50 }),
    refetchInterval: 5000,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteRecording(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['recordings'] }),
  });

  const recordings = data?.data ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Recordings</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Captured browsing sessions. Start one from the extension side panel.
          </p>
        </div>
        <Button asChild variant="outline">
          <Link to="/recipes">
            <BookMarked className="mr-2 size-4" />
            Recipes
          </Link>
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>
          ) : recordings.length === 0 ? (
            <div className="flex flex-col items-center gap-2 p-12 text-center">
              <Radio className="size-8 text-muted-foreground/50" />
              <p className="font-medium">No recordings yet</p>
              <p className="max-w-sm text-sm text-muted-foreground">
                Open the DataBro extension side panel on a page you want to scrape, name your
                recording, and press Start.
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Responses</TableHead>
                  <TableHead>Started</TableHead>
                  <TableHead className="w-20" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {recordings.map((r) => (
                  <TableRow key={r.id} className="group">
                    <TableCell>
                      <Link to={`/recordings/${r.id}`} className="font-medium hover:underline">
                        {r.name}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Badge variant={statusVariant(r.status)} className="capitalize">
                        {r.status === 'recording' && (
                          <span className="mr-1 inline-block size-1.5 animate-pulse rounded-full bg-current" />
                        )}
                        {r.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {r.responseCount ?? r.matchCount}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {new Date(r.startedAt).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-8 opacity-0 group-hover:opacity-100"
                          onClick={() => {
                            if (confirm(`Delete recording "${r.name}"?`)) deleteMutation.mutate(r.id);
                          }}
                        >
                          <Trash2 className="size-4 text-destructive" />
                        </Button>
                        <Button asChild variant="ghost" size="icon" className="size-8">
                          <Link to={`/recordings/${r.id}`}>
                            <ChevronRight className="size-4" />
                          </Link>
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
